import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { ToastList } from '@/components/Toast'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: number
  message: string
  type: ToastType
  title?: string
  durationMs: number
  exiting?: boolean
}

type ToastOptions = {
  type?: ToastType
  title?: string
  durationMs?: number
}

type ToastContextValue = {
  toasts: ToastItem[]
  toast: (message: string, typeOrOptions?: ToastType | ToastOptions) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 0
const DEFAULT_DURATION = 5000
const EXIT_MS = 320
const MAX_TOASTS = 4

function normalizeOptions(
  typeOrOptions?: ToastType | ToastOptions
): ToastOptions {
  if (typeOrOptions == null) return {}
  if (typeof typeOrOptions === 'string') return { type: typeOrOptions }
  return typeOrOptions
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const exitTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const removeToast = useCallback((id: number) => {
    const t = timersRef.current.get(id)
    if (t) {
      clearTimeout(t)
      timersRef.current.delete(id)
    }
    const et = exitTimersRef.current.get(id)
    if (et) {
      clearTimeout(et)
      exitTimersRef.current.delete(id)
    }
    setToasts((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const dismiss = useCallback(
    (id: number) => {
      const t = timersRef.current.get(id)
      if (t) {
        clearTimeout(t)
        timersRef.current.delete(id)
      }
      setToasts((prev) =>
        prev.map((item) => (item.id === id ? { ...item, exiting: true } : item))
      )
      const exitTimer = setTimeout(() => removeToast(id), EXIT_MS)
      exitTimersRef.current.set(id, exitTimer)
    },
    [removeToast]
  )

  const toast = useCallback(
    (message: string, typeOrOptions?: ToastType | ToastOptions) => {
      const trimmed = message.trim()
      if (!trimmed) return

      const opts = normalizeOptions(typeOrOptions)
      const type = opts.type ?? 'info'
      const durationMs = opts.durationMs ?? (type === 'error' ? 6500 : DEFAULT_DURATION)
      const id = nextId++

      setToasts((prev) => {
        const withoutDup = prev.filter(
          (t) => !(t.message === trimmed && t.type === type)
        )
        const next = [...withoutDup, { id, message: trimmed, type, title: opts.title, durationMs }]
        return next.slice(-MAX_TOASTS)
      })

      const timer = setTimeout(() => dismiss(id), durationMs)
      timersRef.current.set(id, timer)
    },
    [dismiss]
  )

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <ToastList toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
